import { useTranslator } from '@bluebeam/react-i18n';
import Validate, { Validator } from 'components/hoc/Validate/Validate';
import { SelectOption } from 'components/inputs/BaseSelect';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import ValidatableInput from 'components/inputs/ValidatableInput';
import { Button } from 'components/lib';
import Tooltip from 'components/Tooltip';
import TooltipButton from 'components/Tooltip/TooltipButton';
import convert from 'convert-units';
import React, { useState } from 'react';
import { Scale } from 'records/MappedSheetVersion';
import {
  IsFraction,
  IsGreaterThan,
  IsNotEmpty,
  IsNumeric,
} from 'utils/Validate';
import BackButton from '../BackButton';
import { useCalibrationMachine } from '../CalibrationMachine';

type SheetScalerProps = {
  onBack: () => void;
  onCancel: () => void;
  onSubmit: (scale: Scale) => void;
};

const convertFraction = (value: string): string => {
  const inputValueString = value;

  if (!IsFraction(inputValueString)) {
    return value;
  }

  const [numerator, denominator] = inputValueString
    .split('/')
    .map((val) => parseInt(val, 10));

  const newValue = denominator === 0 ? 0 : numerator / denominator;

  return parseFloat(newValue.toFixed(5)).toString();
};

const onUnitChange = (
  value: string,
  unit: SmallUnit | LargeUnit,
  setState: (newValue: string) => void,
  previousUnit?: SmallUnit | LargeUnit
) => {
  if (!previousUnit) {
    return;
  }

  const newValue = parseFloat(
    convert(parseFloat(value)).from(previousUnit).to(unit).toFixed(5)
  );

  setState(newValue.toString());
};

const SheetScalerForm: React.FC<SheetScalerProps> = ({
  onBack,
  onCancel,
  onSubmit,
}: SheetScalerProps) => {
  const { calibrationMachineState } = useCalibrationMachine();
  const [measurementValidations, setMeasurementValidations] = useState<
    Validator[]
  >([]);
  const { translate } = useTranslator(['mapView', 'buttons']);

  const localStorageLangName = 'BB_LANGUAGE_CODE';
  const languageCode = localStorage.getItem(localStorageLangName);

  const defaultImperialSystemFromUnit = {
    label: `${translate('mapView:units.in')}` as const,
    value: 'in' as const,
  };

  const defaultMetricSystemFromUnit = {
    label: `${translate('mapView:units.cm')}` as const,
    value: 'cm' as const,
  };

  const defaultImperialSystemToUnit = {
    label: `${translate('mapView:units.ft')}` as const,
    value: 'ft' as const,
  };

  const defaultMetricSystemToUnit = {
    label: `${translate('mapView:units.m')}` as const,
    value: 'm' as const,
  };

  const renderDefaultFromUnit = () => {
    if (languageCode !== 'en-US') {
      return defaultMetricSystemFromUnit;
    }

    return defaultImperialSystemFromUnit;
  };

  const renderDefaultToUnit = () => {
    if (languageCode !== 'en-US') {
      return defaultMetricSystemToUnit;
    }

    return defaultImperialSystemToUnit;
  };

  const FROM_UNITS: SelectOption<SmallUnit>[] = [
    defaultImperialSystemFromUnit,
    {
      label: `${translate('mapView:units.cm')}`,
      value: 'cm',
    },
    {
      label: `${translate('mapView:units.mm').toLowerCase()}`,
      value: 'mm',
    },
  ];

  const TO_UNITS: SelectOption<LargeUnit>[] = [
    {
      label: `${translate('mapView:units.in')}`,
      value: 'in',
    },
    defaultImperialSystemToUnit,
    {
      label: `${translate('mapView:units.yd')}`,
      value: 'yd',
    },
    {
      label: `${translate('mapView:units.mi')}`,
      value: 'mi',
    },
    {
      label: `${translate('mapView:units.mm').toLowerCase()}`,
      value: 'mm',
    },
    {
      label: `${translate('mapView:units.cm')}`,
      value: 'cm',
    },
    {
      label: `${translate('mapView:units.m')}`,
      value: 'm',
    },
    {
      label: `${translate('mapView:units.km')}`,
      value: 'km',
    },
  ];

  const startValidations = () => {
    setMeasurementValidations([
      {
        name: 'required',
        message: 'A value is required',
        test: (value: string): boolean => {
          return IsNotEmpty(value);
        },
      },
      {
        name: 'number',
        message: 'The value must be a number or fraction',
        test: (value: string): boolean => {
          return IsNumeric(value) || IsFraction(value);
        },
      },
      {
        name: 'positive',
        message: 'The value may not be less than or equal to 0',
        test: (value: string): boolean => {
          return IsGreaterThan(0, parseFloat(value));
        },
      },
    ]);
  };

  const {
    calibrationConfig: { scale: initialValues },
  } = calibrationMachineState.context;

  const [fromUnit, setFromUnit] = useState<SelectOption<SmallUnit>>(
    FROM_UNITS.find(
      ({ value }) =>
        value.toLowerCase() === initialValues?.from?.unit.toLowerCase()
    ) || renderDefaultFromUnit()
  );
  const [toUnit, setToUnit] = useState<SelectOption<LargeUnit>>(
    TO_UNITS.find(
      ({ value }) =>
        value.toLowerCase() === initialValues?.to?.unit.toLowerCase()
    ) || renderDefaultToUnit()
  );

  const [fromMeasurement, setFromMeasurement] = useState<string>(
    initialValues?.from?.value ? initialValues.from.value.toString() : ''
  );

  const [toMeasurement, setToMeasurement] = useState<string>(
    initialValues?.to?.value ? initialValues.to.value.toString() : ''
  );

  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  const hasNoMeasurementErrors = measurementValidations.every(
    (validation) =>
      validation.test(fromMeasurement) && validation.test(toMeasurement)
  );

  const isValid =
    hasNoMeasurementErrors &&
    (!isInitialLoad || (fromMeasurement && toMeasurement));

  return (
    <div className="w100 column">
      <div className="row mx3 my2 justify-between items-center">
        <div className="font-primary font-size-5 black-2 row items-center">
          <BackButton onClick={onBack} />
          <span className="medium mr1">
            {translate('mapView:sheetScalerForm.stepOneOfThree')}{' '}
          </span>
          {translate('mapView:sheetScalerForm.setScale')}
          <Tooltip
            title={translate('mapView:sheetScalerForm.scalingDetails')}
            content={
              <>
                <p className="font-size-4 normal mb3">
                  {translate('mapView:sheetScalerForm.scalingDetailsTextOne')}
                </p>
                <p className="font-size-4 normal m0">
                  {translate('mapView:sheetScalerForm.scalingDetailsTextTwo')}
                </p>
              </>
            }
          >
            <TooltipButton variant="info" />
          </Tooltip>
        </div>
        <div className="row">
          <Button
            id="map-prepare-scale-cancel-btn"
            className="mr3"
            variant="tertiary"
            onClick={onCancel}
          >
            {translate('buttons:cancel')}
          </Button>
          <Button
            id="map-prepare-scale-next-btn"
            variant="primary"
            disabled={!isValid}
            onClick={() => {
              const scale: Scale = {
                from: {
                  value: parseFloat(fromMeasurement),
                  unit: fromUnit.value,
                },
                to: {
                  value: parseFloat(toMeasurement),
                  unit: toUnit.value,
                },
              };
              onSubmit(scale);
            }}
          >
            {translate('buttons:nextStep')}
          </Button>
        </div>
      </div>
      <div className="flex my2 justify-center">
        <form>
          <div className="row">
            <div className="column wrem-9 mr3">
              <Label label={translate('mapView:sheetScalerForm.measurement')} />
              <Validate validators={measurementValidations}>
                {({
                  valid,
                  onBlur,
                  onFocus,
                  onChange,
                  onLoad,
                  tested,
                }): React.ReactNode => (
                  <ValidatableInput
                    id="sheet-scale-from"
                    isValid={valid}
                    onBlur={(event) => {
                      setFromMeasurement(convertFraction(event.target.value));
                      onBlur(event);
                    }}
                    onFocus={(event) => {
                      setIsInitialLoad(false);
                      startValidations();
                      onFocus(event);
                    }}
                    onChange={(event) => {
                      setFromMeasurement(event.target.value.trim());
                      onChange(event);
                    }}
                    onInputLoad={onLoad}
                    tested={tested}
                    value={fromMeasurement}
                  />
                )}
              </Validate>
            </div>
            <div className="column ws5">
              <Label label={translate('mapView:sheetScalerForm.unit')} />
              <SingleSelect
                id="sheet-scale-form"
                options={FROM_UNITS}
                value={fromUnit}
                onChange={(event) => {
                  onUnitChange(
                    fromMeasurement,
                    event.value as SmallUnit,
                    setFromMeasurement,
                    fromUnit?.value as SmallUnit
                  );
                  setFromUnit(event as SelectOption<SmallUnit>);
                }}
              />
            </div>
            <div className="mt6 px4 font-size-7 medium"> = </div>
            <div className="column wrem-9 mr3">
              <Label label={translate('mapView:sheetScalerForm.measurement')} />
              <Validate validators={measurementValidations}>
                {({
                  valid,
                  onBlur,
                  onFocus,
                  onChange,
                  onLoad,
                  tested,
                }): React.ReactNode => (
                  <ValidatableInput
                    id="sheet-scale-to"
                    isValid={valid}
                    onBlur={(event) => {
                      setToMeasurement(convertFraction(event.target.value));
                      onBlur(event);
                    }}
                    onFocus={(event) => {
                      setIsInitialLoad(false);
                      startValidations();
                      onFocus(event);
                    }}
                    onChange={(event) => {
                      setToMeasurement(event.target.value.trim());
                      onChange(event);
                    }}
                    onInputLoad={onLoad}
                    tested={tested}
                    value={toMeasurement}
                  />
                )}
              </Validate>
            </div>
            <div className="column ws5">
              <Label label={translate('mapView:sheetScalerForm.unit')} />
              <SingleSelect
                id="sheet-scale-form"
                options={TO_UNITS}
                value={toUnit}
                onChange={(event) => {
                  onUnitChange(
                    toMeasurement,
                    event.value as LargeUnit,
                    setToMeasurement,
                    toUnit?.value as LargeUnit
                  );
                  setToUnit(event as SelectOption<LargeUnit>);
                }}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SheetScalerForm;
